/**
 * ESTADO DE LA IMPRESORA TÉRMICA.
 *
 * La conexión de verdad vive en `lib/impresora.ts`, fuera de React. Este archivo
 * es solo la parte que las pantallas necesitan ver: si hay impresora, cómo está
 * configurada y si está imprimiendo ahora mismo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  REGLA: LA IMPRESORA NUNCA IMPIDE VENDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ninguna función de aquí lanza un error hacia arriba. Si la impresora está
 * apagada, sin papel o desenchufada, `imprimir()` devuelve `false` y la venta
 * sigue su curso — el recibo se queda en pantalla y se puede reimprimir o mandar
 * por WhatsApp.
 *
 * Es una decisión deliberada: un cajero con cola de gente no puede quedarse
 * bloqueado porque a la impresora se le acabó el papel.
 */

import { create } from "zustand";
import {
  CONFIG_POR_DEFECTO,
  ErrorDeImpresora,
  cerrarCanal,
  emparejarBluetooth,
  emparejarUSB,
  guardarConfig,
  hayImpresoraConectada,
  imprimirBytes,
  leerConfig,
  olvidarImpresora,
  reconectar,
  soportaBluetooth,
  soportaImpresionDirecta,
  soportaUSB,
  type ConfigImpresora,
} from "@/lib/impresora";
import { aviso } from "@/stores/ui";

type EstadoImpresora = {
  config: ConfigImpresora;
  /** La impresora responde ahora mismo. */
  conectada: boolean;
  /** Hay un ticket saliendo: sirve para deshabilitar el botón y no imprimir doble. */
  imprimiendo: boolean;
  /** Último problema, para mostrarlo en la pantalla de ajustes. */
  error: { mensaje: string; consejo: string } | null;

  /** Se llama una vez al arrancar la app. Reconecta en silencio si había impresora. */
  iniciar: () => Promise<void>;
  emparejarUsb: () => Promise<boolean>;
  emparejarBt: () => Promise<boolean>;
  olvidar: () => Promise<void>;
  reintentar: () => Promise<boolean>;
  cambiar: (cambios: Partial<ConfigImpresora>) => void;
  /** Manda bytes a la impresora. Devuelve `false` si no se pudo, sin lanzar error. */
  imprimir: (bytes: Uint8Array, opciones?: { silencioso?: boolean }) => Promise<boolean>;
};

/** Traduce cualquier fallo a algo que un dueño de negocio pueda leer. */
function aMensaje(e: unknown): { mensaje: string; consejo: string } {
  if (e instanceof ErrorDeImpresora) return { mensaje: e.message, consejo: e.consejo };

  // Cuando el usuario cierra el diálogo de elegir impresora, el navegador lanza
  // un NotFoundError. No es un error: es que cambió de opinión.
  if (e instanceof DOMException && (e.name === "NotFoundError" || e.name === "AbortError")) {
    return { mensaje: "", consejo: "" };
  }

  return {
    mensaje: "No se pudo comunicar con la impresora.",
    consejo: e instanceof Error ? e.message : String(e),
  };
}

export const useImpresora = create<EstadoImpresora>((set, get) => ({
  config: typeof window === "undefined" ? { ...CONFIG_POR_DEFECTO } : leerConfig(),
  conectada: false,
  imprimiendo: false,
  error: null,

  iniciar: async () => {
    const config = leerConfig();
    set({ config });
    if (config.conexion === null) return;

    // Sin diálogos ni avisos: si la impresora está apagada, simplemente no se
    // conecta y ya se intentará cuando alguien imprima.
    const conectada = await reconectar();
    set({ conectada });
  },

  emparejarUsb: async () => {
    try {
      const config = await emparejarUSB();
      set({ config, conectada: hayImpresoraConectada(), error: null });
      aviso.exito("Impresora conectada", config.nombre);
      return true;
    } catch (e) {
      const error = aMensaje(e);
      if (error.mensaje) set({ error, conectada: false });
      return false;
    }
  },

  emparejarBt: async () => {
    try {
      const config = await emparejarBluetooth();
      set({ config, conectada: hayImpresoraConectada(), error: null });
      aviso.exito("Impresora conectada", config.nombre);
      return true;
    } catch (e) {
      const error = aMensaje(e);
      if (error.mensaje) set({ error, conectada: false });
      return false;
    }
  },

  olvidar: async () => {
    await olvidarImpresora();
    set({ config: leerConfig(), conectada: false, error: null });
    aviso.exito("Impresora olvidada", "Se volverá a imprimir con el diálogo del navegador.");
  },

  reintentar: async () => {
    await cerrarCanal();
    const conectada = await reconectar();
    set({ conectada, error: null });
    if (!conectada) {
      set({
        error: {
          mensaje: "No respondió.",
          consejo: "Comprueba que esté encendida, con papel y — si es Bluetooth — cerca.",
        },
      });
    }
    return conectada;
  },

  cambiar: (cambios) => {
    const config = { ...get().config, ...cambios };
    guardarConfig(config);
    set({ config });
  },

  imprimir: async (bytes, opciones = {}) => {
    if (get().imprimiendo) return false;
    set({ imprimiendo: true, error: null });

    try {
      const copias = Math.max(1, Math.min(3, get().config.copias));
      for (let i = 0; i < copias; i++) {
        await imprimirBytes(bytes);
      }
      set({ conectada: true });
      return true;
    } catch (e) {
      const error = aMensaje(e);
      set({ conectada: hayImpresoraConectada(), error });
      // En el POS el aviso se muestra; en una reimpresión de fondo, no molesta.
      if (!opciones.silencioso && error.mensaje) {
        aviso.error(error.mensaje, error.consejo || undefined);
      }
      return false;
    } finally {
      set({ imprimiendo: false });
    }
  },
}));

/** Reexportadas para que las pantallas no tengan que importar de dos sitios. */
export { soportaUSB, soportaBluetooth, soportaImpresionDirecta };
